import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import blueWave from '@/assets/animations/blue-wave.json';
import purpleWave from '@/assets/animations/purple-wave.json';
import { EquinetPremium, RightArrowIcon, EquinetTeams } from '@/assets/svg';
import GiftIcon from '@/assets/svg/GiftIcon';
import { Button, Modal } from '@/components';
import { useIsPremiumFeatureEnabled, useTrialEligibility } from '@/hooks';
import { useCanManageSubscription } from '@/hooks/useCanManageSubscription';
import { ROUTE } from '@/router/routes';
import { t } from '@/services/translations/config';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';

import { Props } from './types';

const colorsMap = {
    personal: {
        background: 'linear-gradient(to bottom, #E1ECF8, #91B6DE)',
        border: '#9BB7D6',
        text: COLOR.eastBay,
        primary: COLOR.deepCerulean,
    },
    teams: {
        background: 'linear-gradient(to bottom, #F7F5FE, #CEC7E9)',
        border: '#B5B1CE',
        text: '#60576F',
        primary: ENTITY_COLOR.teams,
    },
} as const;

const animationMap = {
    personal: blueWave,
    teams: purpleWave,
} as const;

export const BlockedPremiumFeatureModal: React.FC<Props> = ({
    close,
    description = t('PremiumActionModal:content:personal'),
    info = t('PremiumActionModal:common_footer_text'),
    variant = 'personal',
}) => {
    const { isPremiumFeatureEnabled } = useIsPremiumFeatureEnabled();
    const navigate = useNavigate();

    const isCurrentOrganisationOwner = useCanManageSubscription();
    const isFreeTrialEligible = useTrialEligibility();

    const colors = colorsMap[variant] || colorsMap.personal;

    if (!isPremiumFeatureEnabled || !isCurrentOrganisationOwner) {
        return null;
    }

    return (
        <Modal isOpen center styles="p-0 max-w-[515px]">
            <Box className="flex flex-col overflow-hidden relative gap-6 p-8 rounded-t-lg">
                <Box
                    className="absolute right-0 bottom-0 left-0 -z-1 h-[250px]"
                    sx={{
                        background: colors.background,
                        border: `1px solid ${colors.border}`,
                    }}
                />
                <Lottie
                    className="absolute right-0 bottom-0 left-0 opacity-30 -z-1"
                    animationData={animationMap[variant]}
                />

                <Box className="flex relative gap-5 z-1 justify-between">
                    <Box className="flex relative flex-col gap-4">
                        <Box className="flex gap-2">
                            {variant === 'teams' ? (
                                <EquinetTeams
                                    width={214}
                                    color={colors.primary}
                                />
                            ) : (
                                <EquinetPremium
                                    width={214}
                                    color={colors.primary}
                                />
                            )}
                        </Box>
                    </Box>

                    {isFreeTrialEligible && (
                        <Box
                            className="flex relative gap-2 justify-center items-center self-start px-4 py-2 rounded-full z-2 flex-shrink-0"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.25)',
                            }}
                        >
                            <GiftIcon
                                width={16}
                                height={16}
                                color={colors.primary}
                            />
                            <Typography
                                fontSize={14}
                                fontWeight={700}
                                color={colors.primary}
                            >
                                {t('SubscriptionCommon:free_trial_label')}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Typography
                    color={colors.text}
                    fontSize={14}
                    sx={{ zIndex: 1 }}
                    lineHeight="26px"
                >
                    {description}
                </Typography>
            </Box>

            <Box className="flex flex-col gap-7 mx-8 my-7">
                <Box
                    className="flex gap-3 p-4 rounded-lg"
                    sx={{ backgroundColor: COLOR.foam }}
                >
                    <InfoOutlineIcon
                        style={{
                            width: 20,
                            height: 20,
                            color: COLOR.deepCerulean,
                        }}
                    />
                    <Box
                        sx={{ color: COLOR.astronautBlue }}
                        className="flex flex-col gap-4 text-[12px]"
                    >
                        {info}
                    </Box>
                </Box>

                <Box className="flex gap-2 self-end">
                    <Button
                        sx={{ color: COLOR.tundora }}
                        caption={t('Actions:not_right_now')}
                        onClick={close}
                    />
                    <Button
                        sx={{
                            backgroundColor: colors.primary,
                            color: COLOR.white,
                            '&:hover': {
                                backgroundColor: colors.primary,
                                opacity: 0.8,
                            },
                        }}
                        variant="contained"
                        caption={t('Actions:learn_more')}
                        endIcon={<RightArrowIcon color={COLOR.white} />}
                        onClick={() => navigate(ROUTE.premium)}
                    />
                </Box>
            </Box>
        </Modal>
    );
};
